import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { propType } from 'graphql-anywhere'
import { format, parseISO } from 'date-fns'
import classNames from 'classnames'
import Collapse, { Panel } from 'rc-collapse'
import Table from '../../../shared/settings/async-table'
import { DevicePanelBody, DevicePanelHeader } from './panel-components'
import RoundIcon from '../../../shared/settings/round-icon'
import closeIcon from '../../../../../images/closeModal.svg'

import styles from './devices.css'

import DEVICE_FRAGMENT from '../../../../../graphql/fragments/device.gql'

const downloadAvailable = ['android', 'ios']

export const formatDate = (date) => {
  return format(parseISO(date), 'dd/MM/yyyy')
}

export const NoData = () => {
  return (
    <div className={styles.noData}>
      You haven&apos;t registered any saved devices yet.
    </div>
  )
}

export const RemoveDeviceIcon = ({ onClick }) => {
  return (
    <div className={styles.removeButton} onClick={onClick}>
      <RoundIcon alt="Remove Item" src={closeIcon} />
    </div>
  )
}

RemoveDeviceIcon.propTypes = {
  onClick: PropTypes.func.isRequired
}

export const getTableColumns = ({ handleDeviceRemoval, handleViewDownloads }) => {
  return [
    {
      Header: 'Device Name',
      id: 'name',
      accessor: 'name',
      Cell: d => (<span className={`${styles.rowInfo} ${styles.deviceName}`}>{d.value}</span>),
      minWidth: 40
    },
    {
      Header: 'Registered',
      id: 'firstSeen',
      accessor: 'firstSeen',
      Cell: d => (<span className={styles.rowInfo}>{formatDate(d.value)}</span>),
      minWidth: 20
    },
    {
      Header: 'Last Used',
      id: 'lastSeen',
      accessor: 'lastSeen',
      Cell: d => (<span className={styles.rowInfo}>{formatDate(d.value)}</span>),
      minWidth: 20
    },
    {
      Header: 'Downloads',
      id: 'numberOfDownloads',
      accessor: 'numberOfDownloads',
      Cell: d => {
        const platform = d.original?.platform?.toLowerCase()
        const canViewDownloads = downloadAvailable.includes(platform)

        return canViewDownloads
          ? (
            <span className={styles.rowInfo}>
              (
              {d.value}
              )
              {' '}
              <span
                className={classNames({
                  [styles.deviceDownloads]: true,
                  [styles.clickable]: +d.value > 0
                })}
                onClick={() => {
                  return +d.value > 0 && handleViewDownloads(d.original.uuid)
                }}
              >
                view
              </span>
            </span>
          )
          : (
            <span className={styles.rowInfo}>Not available</span>
          )
      },
      minWidth: 20
    },
    {
      Header: 'Remove',
      id: 'remove',
      Cell: d => (
        <span className={styles.rowInfo}>
          <RemoveDeviceIcon
            onClick={() => {
              handleDeviceRemoval(d.original.uuid)
            }}
          />
        </span>
      ),
      minWidth: 20
    }
  ]
}

export const DesktopTable = ({
  className,
  devices,
  tableColumns,
  handleTableProps,
  handleTableDataProps,
  handleTableHeadProps,
  handleTableRowGroupProps,
  handleTableHeadThProps
}) => {
  return (
    <Table
      className={classNames(className)}
      data={devices}
      columns={tableColumns}
      showPagination={false}
      getTheadThProps={handleTableHeadThProps}
      getTheadProps={handleTableHeadProps}
      getTrGroupProps={handleTableRowGroupProps}
      getTableProps={handleTableProps}
      getTdProps={handleTableDataProps}
      NoDataComponent={NoData}
    />
  )
}

DesktopTable.propTypes = {
  className: PropTypes.string,
  devices: PropTypes.arrayOf(propType(DEVICE_FRAGMENT)).isRequired,
  tableColumns: PropTypes.arrayOf(PropTypes.shape({
    Cell: PropTypes.func.isRequired,
    Header: PropTypes.string.isRequired,
    accessor: PropTypes.string,
    id: PropTypes.string.isRequired,
    minWidth: PropTypes.number
  })).isRequired,
  handleTableHeadThProps: PropTypes.func.isRequired,
  handleTableHeadProps: PropTypes.func.isRequired,
  handleTableRowGroupProps: PropTypes.func.isRequired,
  handleTableProps: PropTypes.func.isRequired,
  handleTableDataProps: PropTypes.func.isRequired
}

DesktopTable.defaultProps = {
  className: ''
}

export const MobileTable = ({
  devices,
  tableColumns
}) => {
  const [activePanel, setActivePanel] = useState(null)

  return (
    <Collapse
      accordion
      onChange={setActivePanel}
    >
      {
        devices.map(
          device => (
            <Panel
              key={device.uuid}
              showArrow={false}
              className={
                classNames(
                  styles.deviceCollapseItem,
                  { [styles.itemActive]: activePanel === device.uuid }
                )
              }
              header={(
                <DevicePanelHeader
                  deviceName={device.name}
                  isOpen={activePanel === device.uuid}
                />
              )}
            >
              <DevicePanelBody deviceProps={device} columns={tableColumns} />
            </Panel>
          )
        )
      }
    </Collapse>
  )
}

MobileTable.propTypes = {
  devices: PropTypes.arrayOf(propType(DEVICE_FRAGMENT)),
  tableColumns: PropTypes.arrayOf(PropTypes.shape({
    Cell: PropTypes.func.isRequired,
    Header: PropTypes.string.isRequired,
    accessor: PropTypes.string,
    id: PropTypes.string.isRequired,
    minWidth: PropTypes.number
  })).isRequired
}

MobileTable.defaultProps = {
  devices: []
}
